// The title and length of a Vimeo video, read in the browser (the critic's wrong 3; BUILD-PLAN stage 6
// keeps its oEmbed read as optional): Vimeo's own player (@vimeo/player, bundled from npm) is loaded
// out of sight in an iframe from player.vimeo.com, which the site's Content-Security-Policy allows in
// a frame, and asked for the video's title and duration by postMessage, so nothing is fetched from
// vimeo.com by the page itself. A video whose privacy stops it, or a player that doesn't answer in
// time, gives null, and the editor shows its typed Length box instead.
import { useEffect, useRef, useState } from 'react'
import Player from '@vimeo/player'
import { vimeoSrc } from '../../free/vimeo.js'
import { readLink, sameVideo } from './model.js'

const WAIT_MS = 20000
// how long after the last keystroke the read starts
const SETTLE_MS = 400

// { seconds, title } or null
export function readVimeo(link, { waitMs = WAIT_MS } = {}) {
  const v = readLink(link)
  if (v.state !== 'found' || typeof document === 'undefined') return Promise.resolve(null)
  return new Promise(resolve => {
    const frame = document.createElement('iframe')
    frame.className = 'adc-vimeo-read'
    frame.title = 'Vimeo'
    frame.tabIndex = -1
    frame.setAttribute('aria-hidden', 'true')
    frame.setAttribute('allow', 'autoplay')
    // setAttribute, so the address is the frame's own from the start
    frame.setAttribute('src', vimeoSrc({ id: v.id, hash: v.hash }))
    document.body.appendChild(frame)
    let player = null
    let done = false
    const finish = answer => {
      if (done) return
      done = true
      clearTimeout(timer)
      try { player?.destroy()?.catch?.(() => {}) } catch { /* the frame is going anyway */ }
      frame.remove()
      resolve(answer)
    }
    const timer = setTimeout(() => finish(null), waitMs)
    try {
      player = new Player(frame)
    } catch {
      finish(null)
      return
    }
    Promise.all([player.getDuration(), player.getVideoTitle().catch(() => null)])
      .then(([seconds, title]) => finish(seconds > 0 ? { seconds: Math.round(seconds), title: title ? String(title).trim() : null } : null))
      .catch(() => finish(null))
  })
}

// The length behind what's in a link box, for the editors:
//   const vimeo = useVimeoRead(linkText, { savedUrl, savedSeconds })
//   vimeo.state    'empty' | 'bad' | 'known' (the saved video, its saved length) | 'reading' | 'read' | 'failed'
//   vimeo.seconds  the length, once known or read; vimeo.title Vimeo's title, once read
//   await vimeo.settled()   waits for a read under way (Save waits for it)
export function useVimeoRead(linkText, { savedUrl = null, savedSeconds = null } = {}) {
  const link = readLink(linkText)
  const known = link.state === 'found' && savedSeconds && sameVideo(link.url, savedUrl)
  const key = link.state === 'found' && !known ? `${link.id}:${link.hash || ''}` : null
  const [read, setRead] = useState({ key: null, state: 'idle', seconds: null, title: null })
  const pending = useRef({ key: null, promise: Promise.resolve(null) })

  useEffect(() => {
    if (!key) return undefined
    let alive = true
    let resolveWait
    pending.current = { key, promise: new Promise(r => { resolveWait = r }) }
    setRead({ key, state: 'reading', seconds: null, title: null })
    const t = setTimeout(() => {
      readVimeo(link.url).then(answer => {
        resolveWait(answer)
        if (alive) setRead({ key, state: answer ? 'read' : 'failed', seconds: answer?.seconds ?? null, title: answer?.title ?? null })
      })
    }, SETTLE_MS)
    return () => { alive = false; clearTimeout(t); resolveWait(null) }
  }, [key])

  let state = link.state === 'found' ? (known ? 'known' : (read.key === key ? read.state : 'reading')) : link.state
  if (state === 'idle') state = 'reading'
  const seconds = known ? savedSeconds : (read.key === key ? read.seconds : null)
  return {
    ...link,
    state,
    seconds,
    title: read.key === key ? read.title : null,
    // the read for this link, once it's done, as { seconds, title } or null
    settled: () => (!key ? Promise.resolve(null) : new Promise(resolve => {
      const started = Date.now()
      const check = () => {
        if (pending.current.key === key) pending.current.promise.then(resolve)
        else if (Date.now() - started > 2000) resolve(null)
        else setTimeout(check, 30)
      }
      check()
    })),
  }
}
